import { AddToCart } from '@components/addtocart';
import { CartButton as CartButtonV4 } from '@components/cartbutton/v4';
import { Star } from '@components/star';
import { TInventory } from '@components/types';
import { getCombinedName } from '@components/variant/variantSelector';
import {
    getImageObject,
    getSafeUrl,
    humanizeCurrency,
    sanityIoImageLoader,
    trimToLength
} from '@core/utils';
import { PLACEHOLDERS, SafeImage } from '@components/image';
import { BadgePercent } from 'lucide-react';
import Link from 'next/link';

type Props = {
  element: TInventory;
  isAvailable?: boolean;
};

export const InventoryProductCard = ({ element, isAvailable }: Props) => {
  const product = element?.product;
  const href = `/product/${product?.id}/${getSafeUrl(product?.name)}?store_id=${element?.store_id}`;
  const onSale = element?.mrp > element?.price;
  const off = onSale ? Math.round(((element.mrp - element.price) / element.mrp) * 100) : 0;
  const dim = isAvailable === false || element?.is_available === false ? 'grayscale' : '';

  return (
    <div className="flex gap-3 p-2.5 md:p-3 bg-white rounded-2xl border border-solid border-neutral-200/70 shadow-sm hover:shadow-md transition-shadow min-w-0 relative overflow-hidden">
      {off > 0 && element?.is_available && (
        <div className="absolute top-0 left-0 z-10 flex items-center gap-1 pl-2 pr-3 py-1 rounded-br-2xl bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-sm">
          <BadgePercent size={13} strokeWidth={2.5} />
          <span className="text-xs font-extrabold tracking-wide leading-none">
            {off}% <span className="font-semibold opacity-90">OFF</span>
          </span>
        </div>
      )}
      <Link href={href} prefetch={false} className="flex-shrink-0">
        <SafeImage
          loader={sanityIoImageLoader}
          src={getImageObject(product?.images)?.url}
          alt={
            getImageObject(product?.images, product?.category?.image)?.description ||
            product?.name ||
            'Product'
          }
          width="128"
          height="128"
          fallback={PLACEHOLDERS.product}
          className={`w-24 h-24 md:w-28 md:h-28 object-cover rounded-xl bg-neutral-100 ${dim}`}
        />
      </Link>
      <div className="flex flex-col justify-between gap-1.5 min-w-0 flex-1 font-manrope">
        <div className="flex flex-col gap-0.5 min-w-0">
          <Link href={href} prefetch={false} className="no-underline">
            <div className="font-semibold text-sm md:text-base text-neutral-900 leading-snug line-clamp-2">
              {trimToLength(getCombinedName(product, null, element?.variant), 60)}
            </div>
          </Link>
          <div className="flex items-center gap-2 flex-wrap text-xs text-neutral-500">
            {product?.brand_id && <span className="font-semibold">{product?.brand?.name || 'Brand'}</span>}
            {product?.stats?.rating_overall ? (
              <Star rating={product?.stats?.rating_overall} />
            ) : null}
          </div>
        </div>
        <div className="flex items-end justify-between gap-2">
          {element?.is_available == true ? (
            <div className="flex flex-col leading-tight">
              <span className="text-base md:text-lg font-bold text-neutral-900">
                {humanizeCurrency(element?.price)}
              </span>
              {onSale && (
                <span className="text-xs text-neutral-400">
                  <span className="line-through">{humanizeCurrency(element?.mrp)}</span>
                  <span className="ml-1.5 font-semibold text-emerald-600">Save {humanizeCurrency(element.mrp - element.price)}</span>
                </span>
              )}
            </div>
          ) : (
            <span className="rounded-full bg-neutral-100 text-neutral-500 text-xs font-semibold px-2.5 py-1">
              Unavailable
            </span>
          )}
          {element?.is_available ? (
            <div className="flex-shrink-0">
              <AddToCart
                product={product}
                inventory={element}
                addToCartBag={false}
                BtnType={CartButtonV4}
                isAvailable={isAvailable}
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
